import type { GeoPoint } from '../types/drawing.js';

/** Earth radius in meters (WGS-84 mean) */
const EARTH_RADIUS_M = 6_371_000;

/** Conversion factor: degrees to radians */
const DEG_TO_RAD = Math.PI / 180;

/** Conversion factor: radians to degrees */
const RAD_TO_DEG = 180 / Math.PI;

/** Distance unit codes for TAK */
const DISTANCE_UNIT_METERS = 0;
const DISTANCE_UNIT_KILOMETERS = 1;
const DISTANCE_UNIT_MILES = 2;
const DISTANCE_UNIT_NAUTICAL = 3;

/** Bearing unit codes for TAK */
const BEARING_UNIT_DEGREES = 0;
const BEARING_UNIT_MILS = 1;

/** Meters per mile */
const METERS_PER_MILE = 1_609.344;

/** Meters per nautical mile */
const METERS_PER_NM = 1_852;

/** Mils per degree (NATO) */
const MILS_PER_DEGREE = 17.777778;

/**
 * Calculate the great-circle distance between two points using the Haversine formula.
 * Returns distance in meters.
 */
export function haversineDistance(a: GeoPoint, b: GeoPoint): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const sinHalfLat = Math.sin(dLat / 2);
  const sinHalfLon = Math.sin(dLon / 2);

  const h = sinHalfLat * sinHalfLat
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinHalfLon * sinHalfLon;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/**
 * Calculate the initial bearing from point A to point B.
 * Returns bearing in degrees (0–360).
 */
export function bearingBetween(a: GeoPoint, b: GeoPoint): number {
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2)
    - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  return (Math.atan2(y, x) * RAD_TO_DEG + 360) % 360;
}

/**
 * Calculate the destination point given an origin, bearing, and distance.
 * Bearing in degrees, distance in meters.
 */
export function destinationPoint(origin: GeoPoint, bearing: number, distance: number): GeoPoint {
  const angularDist = distance / EARTH_RADIUS_M;
  const bearRad = toRad(bearing);
  const lat1 = toRad(origin.lat);
  const lon1 = toRad(origin.lon);

  const sinAngDist = Math.sin(angularDist);
  const cosAngDist = Math.cos(angularDist);
  const sinLat1 = Math.sin(lat1);
  const cosLat1 = Math.cos(lat1);

  const lat2 = Math.asin(sinLat1 * cosAngDist + cosLat1 * sinAngDist * Math.cos(bearRad));
  const lon2 = lon1 + Math.atan2(
    Math.sin(bearRad) * sinAngDist * cosLat1,
    cosAngDist - sinLat1 * Math.sin(lat2),
  );

  return { lat: lat2 * RAD_TO_DEG, lon: lon2 * RAD_TO_DEG };
}

/**
 * Approximate a circle as a polygon with the given number of segments.
 * Returns an array of GeoPoints around the circumference.
 */
export function circleToPolygonPoints(
  center: GeoPoint,
  radiusM: number,
  segments: number,
): GeoPoint[] {
  const step = 360 / segments;
  const points: GeoPoint[] = [];

  for (let i = 0; i < segments; i++) {
    points.push(destinationPoint(center, i * step, radiusM));
  }

  return points;
}

/**
 * Compute a 4-corner rectangle from two diagonal corner points.
 * Returns corners in order: topLeft, topRight, bottomRight, bottomLeft.
 */
export function rectangleFromCorners(a: GeoPoint, b: GeoPoint): GeoPoint[] {
  return [
    { lat: a.lat, lon: a.lon },
    { lat: a.lat, lon: b.lon },
    { lat: b.lat, lon: b.lon },
    { lat: b.lat, lon: a.lon },
  ];
}

/**
 * Format a distance in meters to a human-readable string using the given unit code.
 * Unit codes: 0=meters, 1=km, 2=miles, 3=nautical miles.
 */
export function formatDistance(meters: number, unitCode: number): string {
  switch (unitCode) {
    case DISTANCE_UNIT_KILOMETERS:
      return `${(meters / 1000).toFixed(2)} km`;
    case DISTANCE_UNIT_MILES:
      return `${(meters / METERS_PER_MILE).toFixed(2)} mi`;
    case DISTANCE_UNIT_NAUTICAL:
      return `${(meters / METERS_PER_NM).toFixed(2)} nm`;
    case DISTANCE_UNIT_METERS:
    default:
      return `${meters.toFixed(1)} m`;
  }
}

/**
 * Format a bearing in degrees to a human-readable string using the given unit code.
 * Unit codes: 0=degrees, 1=mils.
 */
export function formatBearing(degrees: number, unitCode: number): string {
  switch (unitCode) {
    case BEARING_UNIT_MILS:
      return `${(degrees * MILS_PER_DEGREE).toFixed(0)} mil`;
    case BEARING_UNIT_DEGREES:
    default:
      return `${degrees.toFixed(1)}°`;
  }
}

/** Convert degrees to radians */
function toRad(deg: number): number {
  return deg * DEG_TO_RAD;
}
